import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  CheckCircle2,
  CreditCard,
  FolderOpen,
  Headphones,
  Loader2,
  MessageSquarePlus,
  Radio,
  Send,
  Sparkles,
  Truck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ConsoleShell } from "@/components/ConsoleShell";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatClaimDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function PartCard({ listing, order, stage, onSendOtp, onVerifyOtp, onPayCod, onPayCard, busy }) {
  const [otp, setOtp] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [cardCvc, setCardCvc] = useState("");

  if (!listing) return null;

  return (
    <div className="border border-white/10 bg-obsidian/60 p-5 sm:p-6">
      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric">
        {listing.sku} · {listing.category} · {listing.type}
      </div>
      <div className="mt-1 font-display text-2xl sm:text-3xl tracking-tight">{listing.name}</div>
      <p className="mt-2 text-sm text-white/55 max-w-2xl">{listing.description}</p>
      <div className="mt-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
        {listing.availability_label} · {listing.lead_days} day lead · fits{" "}
        {(listing.fits_brands || []).slice(0, 5).join(" · ")}
      </div>
      <p className="mt-1 text-xs text-white/40">{listing.fits_notes}</p>
      <div className="mt-4 font-display text-3xl tracking-tighter text-cyan-electric">
        {listing.price_usd > 0 ? `$${listing.price_usd}` : "quote"}
      </div>

      {order && (
        <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
          <div className="font-mono-tech text-[10px] uppercase text-white/45">
            Cart · {order.status?.replace(/_/g, " ")}
          </div>
          {order.sales_note && <p className="text-sm text-white/60">{order.sales_note}</p>}

          {stage === "sales_review" && (
            <div className="flex items-center gap-2 text-sm text-white/55">
              <Loader2 className="h-4 w-4 animate-spin text-cyan-electric" />
              Sales team is verifying stock…
            </div>
          )}

          {(stage === "awaiting_otp" || stage === "stock_confirmed") && (
            <div className="space-y-2">
              <p className="text-sm text-white/60">Verify your phone to continue checkout.</p>
              <button
                type="button"
                disabled={busy}
                onClick={onSendOtp}
                className="h-10 px-4 border border-white/20 font-mono-tech text-[10px] uppercase"
              >
                Send verification code
              </button>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6-digit OTP"
                maxLength={6}
                className="w-full h-10 bg-black/50 border border-white/15 px-3 text-center tracking-widest font-mono-tech"
              />
              <button
                type="button"
                disabled={busy || otp.length < 4}
                onClick={() => onVerifyOtp(otp)}
                className="w-full h-10 bg-mclaren text-void font-mono-tech text-[10px] uppercase"
              >
                Verify identity
              </button>
            </div>
          )}

          {stage === "awaiting_payment" && (
            <div className="space-y-3">
              <p className="text-sm text-white/60">Choose how to pay - item is in your cart.</p>
              <button
                type="button"
                disabled={busy}
                onClick={onPayCod}
                className="w-full h-11 flex items-center justify-center gap-2 bg-cyan-electric text-void font-mono-tech text-[10px] uppercase"
              >
                <Truck className="h-4 w-4" /> Cash on delivery
              </button>
              <div className="border border-white/15 bg-black/40 p-3 space-y-2">
                <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase text-white/40">
                  <CreditCard className="h-3.5 w-3.5" /> Pay by card
                </div>
                <input
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="Card number"
                  className="w-full h-9 bg-black/50 border border-white/15 px-3 text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={cardExp}
                    onChange={(e) => setCardExp(e.target.value)}
                    placeholder="MM/YY"
                    className="h-9 bg-black/50 border border-white/15 px-3 text-sm"
                  />
                  <input
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value)}
                    placeholder="CVC"
                    className="h-9 bg-black/50 border border-white/15 px-3 text-sm"
                  />
                </div>
                <button
                  type="button"
                  disabled={busy || cardNumber.replace(/\s/g, "").length < 12}
                  onClick={() => onPayCard(cardNumber.replace(/\s/g, ""))}
                  className="w-full h-10 border border-cyan-electric text-cyan-electric font-mono-tech text-[10px] uppercase"
                >
                  Pay with card
                </button>
              </div>
            </div>
          )}

          {stage === "completed" && (
            <div className="flex items-center gap-2 text-cyan-electric text-sm">
              <CheckCircle2 className="h-5 w-5" />
              Purchase complete - confirmation sent to your email.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const [session, setSession] = useState(null);
  const [claims, setClaims] = useState([]);
  const [viewMode, setViewMode] = useState("active"); // active | history
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [quotaError, setQuotaError] = useState("");
  const bottomRef = useRef(null);
  const isSplit = session?.layout === "split" && session?.listing;
  const isClosed = session?.is_closed || session?.active === false;
  const enquiryLocked = Boolean(session?.enquiry_locked);
  const canType = viewMode === "active" && !isClosed && !enquiryLocked;
  const chatsUsed = session?.chats_used ?? claims.length;
  const chatLimit = session?.chat_limit ?? 5;
  const canStartNew = session?.can_start_new_chat !== false;
  const enquiryCount = session?.enquiry_count ?? 0;
  const enquiryLimit = session?.enquiry_limit ?? 15;
  const planName = session?.plan || "free";

  const loadClaims = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/marketplace/sessions`);
      setClaims(data || []);
    } catch {
      setClaims([]);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const { data } = await axios.get(`${API}/marketplace/session`);
    setSession(data);
    setViewMode("active");
    return data;
  }, []);

  const openClaim = useCallback(async (claimId, reopen = false) => {
    setBusy(true);
    try {
      const url = reopen
        ? `${API}/marketplace/sessions/${claimId}/reopen`
        : `${API}/marketplace/sessions/${claimId}`;
      const { data } = reopen
        ? await axios.post(url)
        : await axios.get(url);
      setSession(data);
      setViewMode(reopen ? "active" : "history");
      await loadClaims();
    } finally {
      setBusy(false);
    }
  }, [loadClaims]);

  const startNewChat = useCallback(async () => {
    setBusy(true);
    setQuotaError("");
    try {
      const { data } = await axios.post(`${API}/marketplace/session/new`);
      setSession(data);
      setViewMode("active");
      setInput("");
      await loadClaims();
    } catch (err) {
      setQuotaError(formatApiErrorDetail(err.response?.data?.detail) || "Chat limit reached.");
    } finally {
      setBusy(false);
    }
  }, [loadClaims]);

  useEffect(() => {
    refreshSession();
    loadClaims();
  }, [refreshSession, loadClaims]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages]);

  useEffect(() => {
    const polling =
      session?.stage === "sales_review" ||
      session?.order?.status === "pending_sales_review" ||
      (viewMode === "active" && session?.support_requested);
    if (!polling) return undefined;
    const t = setInterval(() => {
      refreshSession();
    }, session?.support_requested ? 4000 : 2500);
    return () => clearInterval(t);
  }, [session?.stage, session?.order?.status, session?.support_requested, viewMode, refreshSession]);

  useEffect(() => {
    if (session?.stage === "awaiting_payment" && viewMode === "active") {
      refreshSession();
    }
  }, [session?.stage, viewMode, refreshSession]);

  // Backend closes chats after 10 min idle - refresh periodically
  useEffect(() => {
    if (viewMode !== "active") return undefined;
    const t = setInterval(async () => {
      try {
        await refreshSession();
        await loadClaims();
      } catch {
        /* ignore */
      }
    }, 60000);
    return () => clearInterval(t);
  }, [viewMode, refreshSession, loadClaims]);

  const sendMessage = async (text) => {
    const msg = (text || "").trim();
    if (!msg || busy || !canType) return;
    setBusy(true);
    setQuotaError("");
    try {
      const { data } = await axios.post(`${API}/marketplace/session/message`, { message: msg });
      setSession(data);
      setViewMode("active");
      await loadClaims();
    } catch (err) {
      setQuotaError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send that message.");
    } finally {
      setBusy(false);
    }
  };

  const handleSendOtp = () => sendMessage("SEND OTP");
  const handleVerifyOtp = (code) => code && sendMessage(code);
  const handlePayCod = () => sendMessage("PAY COD");
  const handlePayCard = (digits) => digits && sendMessage(`PAY CARD ${digits}`);

  const messages = session?.messages || [];
  const activeClaimId = session?.id;

  const inputPlaceholder = enquiryLocked
    ? "Enquiry limit reached - sales support will follow up"
    : !canType
      ? "This claim is closed - reopen to continue"
      : session?.support_requested
        ? "Describe your issue while support connects…"
        : session?.stage === "awaiting_details"
          ? "Name, phone, VIN, part needed…"
          : "Type your message…";

  return (
    <ConsoleShell title="marketplace">
      <div className="flex h-[calc(100dvh-4rem)] max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 gap-4 py-3">
        {/* Previous claims sidebar */}
        <aside className="hidden md:flex flex-col w-64 shrink-0 border border-white/10 bg-obsidian/40">
          <div className="p-3 border-b border-white/10">
            <button
              type="button"
              disabled={busy || !canStartNew}
              onClick={startNewChat}
              className="w-full h-10 flex items-center justify-center gap-2 bg-mclaren text-void font-mono-tech text-[10px] uppercase tracking-widest hover:bg-sunset disabled:opacity-50"
            >
              <MessageSquarePlus className="h-4 w-4" /> New chat
            </button>
            <p className="mt-2 font-mono-tech text-[9px] uppercase tracking-widest text-white/40">
              {chatsUsed}/{chatLimit} chats · {planName}
            </p>
          </div>
          <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
            <FolderOpen className="h-3.5 w-3.5 text-cyan-electric" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
              Previous claims
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {claims.length === 0 && (
              <p className="px-2 py-4 text-xs text-white/35 font-mono-tech">No previous chats yet</p>
            )}
            {claims.map((claim) => {
              const selected = claim.id === activeClaimId;
              const closed = !claim.active;
              return (
                <button
                  key={claim.id}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (claim.active) {
                      refreshSession();
                      setViewMode("active");
                    } else {
                      openClaim(claim.id, false);
                    }
                  }}
                  className={`w-full text-left p-2.5 border transition-colors ${
                    selected
                      ? "border-cyan-electric/50 bg-cyan-electric/10"
                      : "border-white/10 bg-black/20 hover:border-white/25"
                  }`}
                >
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-white/40 truncate">
                    {claim.enquiry_locked
                      ? "Sales handoff"
                      : claim.support_requested
                        ? "Support"
                        : claim.stage?.replace(/_/g, " ") || "chat"}
                    {claim.enquiry_locked ? " · locked" : claim.active ? " · live" : " · closed"}
                  </div>
                  <div className="mt-1 text-xs text-white/75 line-clamp-2">{claim.preview}</div>
                  <div className="mt-1 font-mono-tech text-[9px] text-white/35">
                    {formatClaimDate(claim.updated_at)}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="flex flex-col flex-1 min-w-0">
          <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 py-2 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cyan-electric" />
              <span className="font-display text-lg tracking-tight">Mymech parts agent</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy || !canStartNew}
                onClick={startNewChat}
                className="md:hidden h-9 px-3 border border-white/15 font-mono-tech text-[10px] uppercase text-white/60 hover:border-cyan-electric flex items-center gap-1.5 disabled:opacity-40"
              >
                <MessageSquarePlus className="h-3.5 w-3.5" /> New chat
              </button>
              <button
                type="button"
                disabled={busy || !canType}
                onClick={() => sendMessage("I want to talk to support")}
                className="h-9 px-3 border border-white/15 font-mono-tech text-[10px] uppercase text-white/60 hover:border-mclaren hover:text-mclaren flex items-center gap-1.5 disabled:opacity-40"
              >
                <Headphones className="h-3.5 w-3.5" /> Talk to support
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-black/30 px-4 py-2">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
              {chatsUsed}/{chatLimit} chat windows · {enquiryCount}/{enquiryLimit} enquiries this chat
            </span>
            {planName === "free" && (
              <Link
                to="/#pricing"
                className="font-mono-tech text-[10px] uppercase tracking-widest text-mclaren hover:text-sunset"
              >
                Upgrade for more chats
              </Link>
            )}
          </div>

          {quotaError && (
            <div className="mt-3 border border-mclaren/40 bg-mclaren/10 px-4 py-3 text-sm text-white/80">
              {quotaError}{" "}
              {planName === "free" && (
                <Link to="/#pricing" className="text-mclaren underline underline-offset-2">
                  View plans
                </Link>
              )}
            </div>
          )}

          {enquiryLocked && (
            <div className="mt-3 border border-mclaren/40 bg-mclaren/10 px-4 py-3 text-sm text-white/80">
              This chat used all {enquiryLimit} enquiries and was escalated to sales support. Customer chat is locked.
            </div>
          )}

          {viewMode === "history" && isClosed && (
            <div className="mt-3 flex flex-wrap items-center gap-3 border border-white/15 bg-black/40 px-4 py-3">
              <span className="text-sm text-white/60">
                {enquiryLocked ? "This claim is with sales support and cannot be continued." : "Viewing a closed claim."}
              </span>
              {!enquiryLocked && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => openClaim(session.id, true)}
                  className="h-9 px-4 border border-cyan-electric text-cyan-electric font-mono-tech text-[10px] uppercase"
                >
                  Reopen claim
                </button>
              )}
              <button
                type="button"
                disabled={busy || !canStartNew}
                onClick={startNewChat}
                className="h-9 px-4 border border-white/20 font-mono-tech text-[10px] uppercase text-white/60 disabled:opacity-40"
              >
                Start fresh chat
              </button>
            </div>
          )}

          {session?.support_requested && canType && (
            <div className="mt-3 px-4 py-2 border border-mclaren/30 bg-mclaren/5 font-mono-tech text-[10px] uppercase tracking-widest text-mclaren">
              Support connected - keep typing to add details while you wait
            </div>
          )}

          <div
            className={`flex-1 min-h-0 mt-3 mb-3 gap-4 ${
              isSplit ? "grid grid-cols-1 lg:grid-cols-12" : "flex flex-col"
            }`}
          >
            {isSplit && (
              <div className="lg:col-span-8 overflow-y-auto">
                <div className="mb-3">
                  <span className="chip-brand">/ your matched part</span>
                  <h2 className="mt-2 font-display text-2xl tracking-tighter">Added to your cart</h2>
                </div>
                <PartCard
                  listing={session.listing}
                  order={session.order}
                  stage={session.stage}
                  busy={busy}
                  onSendOtp={handleSendOtp}
                  onVerifyOtp={handleVerifyOtp}
                  onPayCod={handlePayCod}
                  onPayCard={handlePayCard}
                />
              </div>
            )}

            <div
              className={`flex flex-col min-h-0 border border-white/10 bg-obsidian/50 ${
                isSplit ? "lg:col-span-4 h-full max-h-full" : "flex-1"
              }`}
            >
              <div className="shrink-0 px-4 py-2 border-b border-white/10 bg-black/40 flex justify-between">
                <span className="font-mono-tech text-[10px] uppercase text-white/50">Agent chat</span>
                <span className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                  {session?.stage?.replace(/_/g, " ") || "…"}
                  {isClosed ? " · closed" : ""}
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {!session && (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-cyan-electric" />
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[95%] px-3 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                        m.role === "user"
                          ? "bg-mclaren/15 border border-mclaren/40"
                          : "bg-black/40 border border-white/10"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 text-sm text-white/40 border border-white/10">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                      Thinking…
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const next = input.trim();
                  if (!next) return;
                  setInput("");
                  sendMessage(next);
                }}
                className="shrink-0 border-t border-white/10 p-3 bg-black/40"
              >
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Radio className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      disabled={busy || !canType}
                      placeholder={inputPlaceholder}
                      className="w-full bg-black/50 border border-white/15 pl-9 pr-3 h-10 font-mono-tech text-sm disabled:opacity-50"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={busy || !input.trim() || !canType}
                    className="h-10 px-4 bg-mclaren text-void disabled:opacity-40 shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                {canType && !isSplit && session?.stage === "greeting" && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["Yes, I want to buy a part", "Hello, I need brake pads"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        disabled={busy}
                        onClick={() => sendMessage(s)}
                        className="text-[10px] px-2 py-1 border border-white/15 text-white/50 hover:border-cyan-electric hover:text-cyan-electric"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </ConsoleShell>
  );
}
